import type {
  NormalizedSourceGroupFields,
  SourceGroupFieldInput,
  SourceGroupType,
} from './types'

export type SourceGroupValidationIssue = {
  path: (keyof NormalizedSourceGroupFields)[]
  message: string
}

export function normalizeSourceGroupFields(
  input: SourceGroupFieldInput
): NormalizedSourceGroupFields {
  const description = input.description ?? null
  const country = input.country ?? null
  const supplierInstallationId = input.supplierInstallationId ?? null

  switch (input.type) {
    case 'INTERNAL':
    case 'UNKNOWN':
      return {
        type: input.type,
        description,
        country: null,
        supplierInstallationId: null,
      }
    case 'EXTERNAL_COUNTRY_KNOWN':
      return {
        type: input.type,
        description,
        country,
        supplierInstallationId: null,
      }
    case 'EXTERNAL_INSTALLATION_KNOWN':
      return {
        type: input.type,
        description,
        country: null,
        supplierInstallationId,
      }
    default: {
      const exhaustive: never = input.type
      return exhaustive
    }
  }
}

export function getSourceGroupValidationIssues(
  fields: NormalizedSourceGroupFields
): SourceGroupValidationIssue[] {
  const issues: SourceGroupValidationIssue[] = []

  switch (fields.type) {
    case 'INTERNAL':
    case 'UNKNOWN':
      if (fields.country) {
        issues.push({
          path: ['country'],
          message: 'Country must not be set for this source group type',
        })
      }
      if (fields.supplierInstallationId) {
        issues.push({
          path: ['supplierInstallationId'],
          message:
            'Supplier installation must not be set for this source group type',
        })
      }
      break
    case 'EXTERNAL_COUNTRY_KNOWN':
      if (!fields.country) {
        issues.push({
          path: ['country'],
          message: 'Country is required for this source group type',
        })
      }
      if (fields.supplierInstallationId) {
        issues.push({
          path: ['supplierInstallationId'],
          message:
            'Supplier installation must not be set for this source group type',
        })
      }
      break
    case 'EXTERNAL_INSTALLATION_KNOWN':
      if (!fields.supplierInstallationId) {
        issues.push({
          path: ['supplierInstallationId'],
          message:
            'Supplier installation is required for this source group type',
        })
      }
      if (fields.country) {
        issues.push({
          path: ['country'],
          message: 'Country must not be set for this source group type',
        })
      }
      break
    default: {
      const exhaustive: never = fields.type
      return exhaustive
    }
  }

  return issues
}

export function applySourceGroupValidationIssuesToZod(
  fields: NormalizedSourceGroupFields,
  addIssue: (issue: {
    code: 'custom'
    message: string
    path: (keyof NormalizedSourceGroupFields)[]
  }) => void
): void {
  for (const issue of getSourceGroupValidationIssues(fields)) {
    addIssue({
      code: 'custom',
      message: issue.message,
      path: issue.path,
    })
  }
}

export function mergeSourceGroupPatchFields(
  existing: NormalizedSourceGroupFields,
  patch: {
    type?: SourceGroupType
    description?: string | null
    country?: string | null
    supplierInstallationId?: string | null
  }
): SourceGroupFieldInput {
  return {
    type: patch.type ?? existing.type,
    description:
      patch.description !== undefined ? patch.description : existing.description,
    country: patch.country !== undefined ? patch.country : existing.country,
    supplierInstallationId:
      patch.supplierInstallationId !== undefined
        ? patch.supplierInstallationId
        : existing.supplierInstallationId,
  }
}
